"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Globe } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Locale,
  LOCALE_LABELS,
  LOCALE_SHORT,
  SUPPORTED_LOCALES,
  isSupportedLocale,
} from "@/i18n/config";
import { writeLocaleCookie } from "@/lib/utils/locale";
import { useAuthStore } from "@/stores/auth-store";
import { apiClient } from "@/lib/api/client";

/**
 * Selector de idioma. Dropdown en el navbar.
 *
 * Al cambiar:
 *   1. Escribe cookie NEXT_LOCALE.
 *   2. Si hay usuario logueado, persiste en `User.locale` vía PUT
 *      `/profile/locale` (best-effort, sin bloquear).
 *   3. `router.refresh()` para que los RSC vuelvan a resolver el locale y
 *      recarguen catálogos + `<html lang>`.
 */
export const LocaleSwitcher = () => {
  const router = useRouter();
  const currentLocale = useLocale() as Locale;
  const t = useTranslations("language_switcher");
  const { user, accessToken } = useAuthStore();

  const change = React.useCallback(
    async (next: Locale) => {
      if (!isSupportedLocale(next)) return;
      if (next === currentLocale) return;

      writeLocaleCookie(next);

      if (user && accessToken) {
        try {
          await apiClient.put("/profile/locale", { locale: next });
        } catch {
          // Best-effort: la cookie ya está puesta y next-intl coge el nuevo
          // locale al refresh. La persistencia server-side se puede reintentar
          // en la siguiente visita.
        }
      }

      router.refresh();
    },
    [currentLocale, router, user, accessToken],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 px-2"
          aria-label={t("aria_label")}
        >
          <Globe className="h-4 w-4" />
          <span className="text-xs font-medium">
            {LOCALE_SHORT[currentLocale]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => change(code)}
            className="cursor-pointer"
          >
            <span className="mr-2 text-xs font-semibold text-muted-foreground">
              {LOCALE_SHORT[code]}
            </span>
            <span>{LOCALE_LABELS[code]}</span>
            {code === currentLocale && (
              <span className="ml-auto text-primary">•</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
